import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { getIcon, ICON_CHOICES } from "@/lib/icons";

export const Field = ({ label, value, onChange, textarea, hint, testId }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</label>
    {textarea ? (
      <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="admin-grid-input" data-testid={testId} />
    ) : (
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="admin-grid-input" data-testid={testId} />
    )}
    {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
  </div>
);

export const ImageField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</label>
    <div className="flex items-start gap-3">
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="https://... atau /assets/namafile.jpg" className="admin-grid-input" data-testid={testId} />
      {value && (
        <img src={value} alt="Pratinjau" className="h-10 w-14 shrink-0 rounded-md border border-slate-200 object-cover" />
      )}
    </div>
    <p className="mt-1 text-xs text-slate-400">Tempel URL gambar, atau letakkan file di folder public/assets lalu isi /assets/namafile</p>
  </div>
);

export const IconField = ({ label, value, onChange, testId }) => {
  const Icon = getIcon(value);
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</label>
      <div className="flex items-center gap-3">
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="admin-grid-input" data-testid={testId}>
          {ICON_CHOICES.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-mist">
          <Icon className="h-5 w-5 text-brand" />
        </span>
      </div>
    </div>
  );
};

export const ListEditor = ({ items, fields, onChange, addLabel, itemTitle, testIdPrefix }) => {
  const [open, setOpen] = useState(0);

  const update = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => {
    const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));
    onChange([...items, blank]);
    setOpen(items.length);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
    setOpen(j);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-list`}>
      {items.map((item, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between px-4 py-3 text-left"
            data-testid={`${testIdPrefix}-item-toggle-${i}`}
          >
            <span className="text-sm font-semibold text-brand-dark">
              {itemTitle ? item[itemTitle] || `Item ${i + 1}` : `Item ${i + 1}`}
            </span>
            <span className="flex items-center gap-1">
              <span onClick={(e) => { e.stopPropagation(); move(i, -1); }} className="rounded p-1 text-slate-400 hover:text-brand" role="button" aria-label="Naik"><ChevronUp className="h-4 w-4" /></span>
              <span onClick={(e) => { e.stopPropagation(); move(i, 1); }} className="rounded p-1 text-slate-400 hover:text-brand" role="button" aria-label="Turun"><ChevronDown className="h-4 w-4" /></span>
              <span onClick={(e) => { e.stopPropagation(); remove(i); }} className="rounded p-1 text-slate-400 hover:text-red-500" role="button" aria-label="Hapus" data-testid={`${testIdPrefix}-item-delete-${i}`}><Trash2 className="h-4 w-4" /></span>
            </span>
          </button>
          {open === i && (
            <div className="grid gap-4 border-t border-slate-100 p-4 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.key} className={f.textarea || f.image ? "sm:col-span-2" : ""}>
                  {f.image ? (
                    <ImageField label={f.label} value={item[f.key]} onChange={(v) => update(i, f.key, v)} testId={`${testIdPrefix}-${i}-${f.key}`} />
                  ) : f.lines ? (
                    <Field label={f.label} value={(item[f.key] || []).join("\n")} onChange={(v) => update(i, f.key, v.split("\n").filter((s) => s.trim()))} textarea testId={`${testIdPrefix}-${i}-${f.key}`} />
                  ) : f.icon ? (
                    <IconField label={f.label} value={item[f.key]} onChange={(v) => update(i, f.key, v)} testId={`${testIdPrefix}-${i}-${f.key}`} />
                  ) : (
                    <Field label={f.label} value={item[f.key]} onChange={(v) => update(i, f.key, v)} textarea={f.textarea} testId={`${testIdPrefix}-${i}-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid={`${testIdPrefix}-add`}
        className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-brand/40 px-5 py-2.5 text-sm font-semibold text-brand transition-colors hover:border-brand hover:bg-brand-mist"
      >
        <Plus className="h-4 w-4" /> {addLabel}
      </button>
    </div>
  );
};
